import type { EditorCommand, PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { Step } from '@atlaskit/editor-prosemirror/transform';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { editorOfflineStoragePluginKey } from './main';
import { fireOfflineStorageAnalyticsEvent, parseErrorMessages } from '../analytics';
import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';

/**
 * Called during the 'restoring' state when there are no steps to apply
 * @returns A command to set the plugin status to 'ready'
 */
export function noStepsCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'ready',
		});
		return tr;
	};
}

/**
 * @param steps Prosemirror steps to apply to the transaction
 * @returns A command to apply the given steps to the transaction, and set the plugin status to 'ready'
 */
export function applyStepsCommand(
	steps: Step[],
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
): EditorCommand {
	return ({ tr }) => {
		const failedSteps: { step: Step; error: Error }[] = [];
		steps.forEach((step) => {
			try {
				tr.step(step);
			} catch (e) {
				// step failed to apply, ignore it and attempt to apply other steps
				failedSteps.push({ step, error: e });
			}
		});

		if (failedSteps.length > 0) {
			fireOfflineStorageAnalyticsEvent({
				api,
				action: ACTION.OFFLINE_STORAGE_FAILED_STEPS,
				errorInfo: parseErrorMessages(failedSteps.map((s) => s.error)),
				stepsCount: failedSteps.length,
			});
		}

		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'ready',
		});
		return tr;
	};
}

/**
 * @returns A command to set the plugin status to 'tooManyRetries'
 */
export function tooManyRetriesCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'tooManyRetries',
		});
		return tr;
	};
}

/**
 * @param retryCount The current number of retry attempts
 * @returns A command to set the plugin status to 'pending' and increment the retry count
 */
export function retryCommand(retryCount: number): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			status: 'pending',
			retryCount: retryCount + 1,
		});
		return tr;
	};
}

/**
 * @returns A command to disable the editor
 */
export function disableEditorCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			disableEditor: true,
		});
		return tr;
	};
}

/**
 * @returns A command to enable the editor
 */
export function enableEditorCommand(): EditorCommand {
	return ({ tr }) => {
		tr.setMeta('addToHistory', false);
		tr.setMeta(editorOfflineStoragePluginKey, {
			disableEditor: false,
		});
		return tr;
	};
}
